import {Dexie} from 'dexie';
import {VanillaBookmarkDataCallback, VanillaVoidCallback} from '../VanillaReader/VanillaReaderModel';
import {IVanillaReaderBookmarkData} from '../VanillaReader/VanillaReaderModel';
import {IVanillaReaderBookmarkDataStore} from './IVanillaReaderBookmarkDataStore';
import {IVanillaReaderBookmarkStoreData} from "./VanillaReaderStoreModel";

/**
 * # VanillaReaderBookmarkIndexedDbDataStore
 *
 *  ## RESPONSIBILITIES
 *
 * This class acts as a runtime storage for the _bookmarks that have been added to a publication.
 * Again, it does not persist its state anywhere. To save _bookmarks between sessions call `getBookmarks()` and
 * store the returned data. To restore state you provide the same data to the `addBookmarks()` method.
 *
 * ## NOTE
 *
 * This is just the runtime storage, the rendering of _bookmarks is done in the `VanillaReaderAnnotationLayerBookmarks`
 * class.
 *
 */
export class VanillaReaderBookmarkIndexedDbDataStore extends Dexie implements IVanillaReaderBookmarkDataStore {
    private _bookmarks: Dexie.Table<IVanillaReaderBookmarkStoreData, string>;

    private _bookmarkAddedCallback: VanillaBookmarkDataCallback | undefined = undefined;
    private _bookmarkDeletedCallback: VanillaBookmarkDataCallback | undefined = undefined;
    private _bookmarksCollectionUpdatedCallback: VanillaVoidCallback | undefined = undefined;

    private _initialized: boolean = false;

    constructor(dbName: string = 'bookmarks.vanillareader.colibrio.com') {
        super(dbName);

        this.version(1).stores({
            bookmarks: '_id, locator, bookSection, publicationId, dateCreated',
        });

        this._bookmarks = this.table('bookmarks');

    }

    initialize(): Promise<void> {
        return this.open().then(_db => {
            this._initialized = true
        }).catch(_ => {
            this._initialized = false
        });
    }

    onBookmarkAdded(callback: VanillaBookmarkDataCallback): void {
        this._bookmarkAddedCallback = callback;
    }

    onBookmarkDeleted(callback: VanillaBookmarkDataCallback): void {
        this._bookmarkDeletedCallback = callback;
    }

    onBookmarksCollectionUpdated(callback: VanillaVoidCallback): void {
        this._bookmarksCollectionUpdatedCallback = callback;
    }

    async addBookmark(bookmarkData: IVanillaReaderBookmarkData): Promise<IVanillaReaderBookmarkData> {
        let targetLocator = bookmarkData.locator;
        if (this._initialized) {
            let targetHasBookmark = await this._bookmarks.where('locator').equals(targetLocator).count() > 0;
            if (!targetHasBookmark) {
                return this._addBookmark(bookmarkData).catch();
            } else {
                console.warn(`VanillaReaderBookmarkStore.addBookmark(): location "${targetLocator}" is already Bookmarked.`);
                return Promise.reject(`VanillaReaderBookmarkStore.addBookmark(): location "${targetLocator}" is already Bookmarked.`);
            }
        } else {
            const message = `VanillaReaderBookmarkStore.addBookmark(): No database connection.`
            console.warn(message);
            return Promise.reject(message);
        }
    }

    async addBookmarks(bookmarkData: IVanillaReaderBookmarkData[], silent: boolean = true): Promise<void> {
        if (this._initialized) {
            bookmarkData.forEach((bookmark: IVanillaReaderBookmarkData) => {
                this._addBookmark(bookmark, silent).catch(console.warn);
            });
        }
        return Promise.resolve();
    }

    async deleteBookmark(locator: string): Promise<void> {
        if (this._initialized) {
            let bookmark = await this._bookmarks.where('locator').equals(locator).first();
            if (bookmark) {
                if (bookmark._id) {
                    this._bookmarks.delete(bookmark._id);

                    const applicationBookmark = VanillaReaderBookmarkIndexedDbDataStore.toApplicationData(bookmark);
                    if (this._bookmarkDeletedCallback) {
                        this._bookmarkDeletedCallback(applicationBookmark);
                    }

                    if (this._bookmarksCollectionUpdatedCallback) {
                        this._bookmarksCollectionUpdatedCallback();
                    }
                }

            } else {
                console.warn(`VanillaReaderBookmarkStore.deleteBookmark(): location "${locator}" is not bookmarked.`);
            }
        } else {
            console.warn(`VanillaReaderBookmarkStore.deleteBookmark(): No database connection`);
        }
    }

    async deleteAllBookmarksByPublication(publicationId: string): Promise<void> {
        if (this._initialized) {
            await this._bookmarks.where('publicationId').equals(publicationId).delete();
        }
        return Promise.resolve();
    }

    async deleteAllBookmarks(): Promise<void> {
        if (this._initialized) {
            await this._bookmarks.each(async (bookmark: IVanillaReaderBookmarkData) => {
                if (bookmark._id) {
                    await this.deleteBookmark(bookmark._id);
                }
            });
        }
        return Promise.resolve();
    }

    async fetchBookmark(locator: string): Promise<IVanillaReaderBookmarkData | undefined> {
        if (this._initialized) {
            let bookmark = await this._bookmarks.where('locator').equals(locator).first()
                .then(VanillaReaderBookmarkIndexedDbDataStore.maybeToApplicationData);
            if (bookmark) {
                return bookmark;
            } else {
                console.warn(`VanillaReaderBookmarkStore.addBookmark(): no bookmark found for location "${locator}".`);
            }
        }
        return;
    }

    async fetchAllBookmarks(): Promise<IVanillaReaderBookmarkData[]> {
        if (this._initialized) {
            return this._bookmarks.toArray().then(bookmarks => bookmarks.map(VanillaReaderBookmarkIndexedDbDataStore.toApplicationData));
        } else {
            return [];
        }
    }

    async fetchBookmarksByPublication(publicationId: string): Promise<IVanillaReaderBookmarkData[]> {
        if (this._initialized) {
            return this._bookmarks.where('publicationId').equals(publicationId).toArray()
                .then(bookmarks => bookmarks.map(VanillaReaderBookmarkIndexedDbDataStore.toApplicationData));
        } else {
            return [];
        }
    }

    /**
     *
     * PRIVATE METHODS
     *
     * */

    private async _addBookmark(
        bookmarkData: IVanillaReaderBookmarkData,
        silent: boolean = false,
    ): Promise<IVanillaReaderBookmarkData> {
        let targetLocator = bookmarkData.locator;
        if (!bookmarkData.dateCreated) {
            bookmarkData.dateCreated = Date.now();
        }

        if (!bookmarkData._id) {
            bookmarkData._id = Date.now().toString();
        }
        const storeData = VanillaReaderBookmarkIndexedDbDataStore.toStoreData(bookmarkData);
        await this._bookmarks.put(storeData, targetLocator);

        if (this._bookmarkAddedCallback) {
            this._bookmarkAddedCallback(bookmarkData, silent);
        }
        if (this._bookmarksCollectionUpdatedCallback) {
            this._bookmarksCollectionUpdatedCallback();
        }

        return bookmarkData;
    }

    private static toStoreData(bookmarkData: IVanillaReaderBookmarkData): IVanillaReaderBookmarkStoreData {
        return {
            _id: bookmarkData._id,
            locator: bookmarkData.locator,
            publicationId: bookmarkData.publicationId,
            color: bookmarkData.color,
            text: bookmarkData.text,
            bookSection: bookmarkData.bookSection,
            dateCreated: bookmarkData.dateCreated
        }
    }

    private static maybeToApplicationData(storeData: IVanillaReaderBookmarkStoreData | undefined): IVanillaReaderBookmarkData | undefined {
        return storeData ? VanillaReaderBookmarkIndexedDbDataStore.toApplicationData(storeData) : undefined;
    }

    private static toApplicationData(storeData: IVanillaReaderBookmarkStoreData): IVanillaReaderBookmarkData {
        return {
            _id: storeData._id,
            locator: storeData.locator,
            publicationId: storeData.publicationId,
            color: storeData.color,
            text: storeData.text,
            bookSection: storeData.bookSection,
            dateCreated: storeData.dateCreated
        }
    }
}
